// src/components/invoices/SplitPaymentSelector.jsx
/**
 * SplitPaymentSelector Component
 * 
 * Allows users to split invoice payments across two different payment methods.
 * This is used for the "Smart Otra" feature where customers pay with two methods
 * (e.g., cash + transfer) but receive a single AFIP invoice with code 208 (Otra).
 * 
 * Key Features:
 * - Real-time validation (amounts must equal invoice total)
 * - Auto-calculation of second payment amount
 * - Visual feedback for validation status
 * - Support for all AFIP payment method codes
 */

import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Alert,
    Typography,
    InputAdornment
} from '@mui/material';

// AFIP Payment Methods - Same as InvoicePreviewModal for consistency
const PAYMENT_METHODS = [
    { code: '201', label: 'Contado (Efectivo)' },
    { code: '202', label: 'Tarjeta de débito' },
    { code: '203', label: 'Tarjeta de crédito' },
    { code: '204', label: 'Cuenta corriente' },
    { code: '205', label: 'Cheque' },
    { code: '206', label: 'Transferencia Bancaria' },
    { code: '207', label: 'Otros medios electrónicos' }
];

/**
 * Get payment method name from code
 * @param {string} code - AFIP payment method code
 * @returns {string} Payment method name
 */
export const getPaymentMethodName = (code) => {
    const method = PAYMENT_METHODS.find(pm => pm.code === code);
    return method ? method.label : 'Desconocido';
};

function SplitPaymentSelector({
    total,
    payment1,
    payment2,
    onPayment1Change,
    onPayment2Change,
    disabled = false
}) {
    const [validationError, setValidationError] = useState('');
    const [isValid, setIsValid] = useState(false);

    // Real-time validation
    useEffect(() => {
        const sum = (payment1.amount || 0) + (payment2.amount || 0);
        const diff = Math.abs(sum - total);

        if (payment1.amount <= 0 && payment2.amount <= 0) {
            setValidationError('Ingrese los montos de pago');
            setIsValid(false);
        } else if (diff > 0.01) { // Allow 1 cent tolerance for rounding
            const missingAmount = total - sum;
            if (missingAmount > 0) {
                setValidationError(
                    `Faltan $${missingAmount.toFixed(2)} para completar el total`
                );
            } else {
                setValidationError(
                    `El total excede en $${Math.abs(missingAmount).toFixed(2)}`
                );
            }
            setIsValid(false);
        } else if (payment1.method === payment2.method) {
            setValidationError('Los métodos de pago deben ser diferentes');
            setIsValid(false);
        } else if (payment1.amount <= 0 || payment2.amount <= 0) {
            setValidationError('Ambos montos deben ser mayores a $0');
            setIsValid(false);
        } else {
            setValidationError('');
            setIsValid(true);
        }
    }, [payment1, payment2, total]);

    // Handle amount 1 change with auto-calculation of amount 2
    const handleAmount1Change = useCallback((value) => {
        const amount = parseFloat(value) || 0;
        onPayment1Change({ ...payment1, amount });

        // Auto-calculate amount2 (remaining balance)
        const remaining = total - amount;
        if (remaining >= 0) {
            onPayment2Change({ ...payment2, amount: Number(remaining.toFixed(2)) });
        }
    }, [payment1, payment2, total, onPayment1Change, onPayment2Change]);

    // Handle amount 2 change with auto-calculation of amount 1
    const handleAmount2Change = useCallback((value) => {
        const amount = parseFloat(value) || 0;
        onPayment2Change({ ...payment2, amount });

        // Auto-calculate amount1 (remaining balance)
        const remaining = total - amount;
        if (remaining >= 0) {
            onPayment1Change({ ...payment1, amount: Number(remaining.toFixed(2)) });
        }
    }, [payment1, payment2, total, onPayment1Change, onPayment2Change]);

    // Calculate percentages for display
    const percentage1 = total > 0 ? ((payment1.amount || 0) / total * 100).toFixed(1) : '0.0';
    const percentage2 = total > 0 ? ((payment2.amount || 0) / total * 100).toFixed(1) : '0.0';

    return (
        <Box>
            {/* Info Alert */}
            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    💡 El sistema generará <strong>UNA factura</strong> usando código AFIP 208 (Otra),
                    pero guardará el detalle de ambos métodos de pago para control de caja y reportes.
                </Typography>
            </Alert>

            <Grid container spacing={2}>
                {/* Payment Method 1 */}
                <Grid item xs={12}>
                    <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                        Método de Pago 1
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={7}>
                    <FormControl fullWidth size="small" disabled={disabled}>
                        <InputLabel id="payment-method-1-label">Método 1</InputLabel>
                        <Select
                            labelId="payment-method-1-label"
                            id="payment-method-1"
                            value={payment1.method}
                            label="Método 1"
                            onChange={(e) => onPayment1Change({
                                ...payment1,
                                method: e.target.value
                            })}
                        >
                            {PAYMENT_METHODS.map(pm => (
                                <MenuItem
                                    key={pm.code}
                                    value={pm.code}
                                    disabled={pm.code === payment2.method}
                                >
                                    {pm.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={5}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Monto 1"
                        type="number"
                        value={payment1.amount || ''}
                        onChange={(e) => handleAmount1Change(e.target.value)}
                        disabled={disabled}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            endAdornment: payment1.amount > 0 && (
                                <InputAdornment position="end">
                                    <Typography variant="caption" color="text.secondary">
                                        ({percentage1}%)
                                    </Typography>
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>

                {/* Payment Method 2 */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                        Método de Pago 2
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={7}>
                    <FormControl fullWidth size="small" disabled={disabled}>
                        <InputLabel id="payment-method-2-label">Método 2</InputLabel>
                        <Select
                            labelId="payment-method-2-label"
                            id="payment-method-2"
                            value={payment2.method}
                            label="Método 2"
                            onChange={(e) => onPayment2Change({
                                ...payment2,
                                method: e.target.value
                            })}
                        >
                            {PAYMENT_METHODS.map(pm => (
                                <MenuItem
                                    key={pm.code}
                                    value={pm.code}
                                    disabled={pm.code === payment1.method}
                                >
                                    {pm.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={5}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Monto 2"
                        type="number"
                        value={payment2.amount || ''}
                        onChange={(e) => handleAmount2Change(e.target.value)}
                        disabled={disabled}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            endAdornment: payment2.amount > 0 && (
                                <InputAdornment position="end">
                                    <Typography variant="caption" color="text.secondary">
                                        ({percentage2}%)
                                    </Typography>
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>

                {/* Validation Summary Box */}
                <Grid item xs={12}>
                    <Box sx={{
                        p: 2,
                        bgcolor: validationError ? '#fff1f0' : '#f6ffed',
                        border: `1px solid ${validationError ? '#ffa39e' : '#b7eb8f'}`,
                        borderRadius: 1,
                        mt: 1
                    }}>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    <strong>Total Factura:</strong>
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" align="right">
                                    ${total.toFixed(2)}
                                </Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="body2">
                                    <strong>Suma Pagos:</strong>
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" align="right">
                                    ${((payment1.amount || 0) + (payment2.amount || 0)).toFixed(2)}
                                </Typography>
                            </Grid>

                            {/* Payment breakdown */}
                            {payment1.amount > 0 && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            • {getPaymentMethodName(payment1.method)}:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" align="right" display="block">
                                            ${payment1.amount.toFixed(2)} ({percentage1}%)
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            {payment2.amount > 0 && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            • {getPaymentMethodName(payment2.method)}:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" align="right" display="block">
                                            ${payment2.amount.toFixed(2)} ({percentage2}%)
                                        </Typography>
                                    </Grid>
                                </>
                            )}
                        </Grid>

                        {/* Validation Status */}
                        {validationError && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                ⚠️ {validationError}
                            </Typography>
                        )}
                        {isValid && (
                            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                                ✓ Montos validados correctamente
                            </Typography>
                        )}
                    </Box>
                </Grid>

                {/* Quick Tip */}
                {!isValid && payment1.amount === 0 && payment2.amount === 0 && (
                    <Grid item xs={12}>
                        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                            <Typography variant="caption">
                                💡 <strong>Tip:</strong> Ingrese el primer monto y el segundo se calculará automáticamente.
                            </Typography>
                        </Alert>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

SplitPaymentSelector.propTypes = {
    /** Invoice total amount */
    total: PropTypes.number.isRequired,
    /** First payment method object { method: string, amount: number } */
    payment1: PropTypes.shape({
        method: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired
    }).isRequired,
    /** Second payment method object { method: string, amount: number } */
    payment2: PropTypes.shape({
        method: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired
    }).isRequired,
    /** Callback when payment1 changes */
    onPayment1Change: PropTypes.func.isRequired,
    /** Callback when payment2 changes */
    onPayment2Change: PropTypes.func.isRequired,
    /** Whether the component is disabled */
    disabled: PropTypes.bool
};

export default SplitPaymentSelector;
